<template>
	<div
		v-if="acceptOnlineOrder"
		class="processing-wrap"
		:class="{ 'processing-wrap--dark': darkTheme }"
	>
		<PageHeader
			:title="venueInfo.name"
			:isLeftButton="isTabFailed"
			route-name="cart"
			@go-back="goBack"
		/>

		<div class="processing-holder">
			<transition-group name="fade">
				<component
					:is="openingTabComponent"
					v-if="!isTabFailed"
					key="processing"
					:dark-theme="darkTheme"
					:customerNameToPrint="customerNameToPrint"
				/>

				<processing-failed
					v-else
					:key="'processing-failed'"
					:customerNameToPrint="customerNameToPrint"
					:isLoading="isLoading"
					:dark-theme="darkTheme"
					@retry="retryOpenTab"
				/>
			</transition-group>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import routerMixin from '@/mixins/routerMixin'

import PageHeader from '@/components/PageHeader'

import RegularTabProcessing from './RegularTabProcessing.vue'
import ProcessingFailed from './ProcessingFailed.vue'
import TicketingTabProcessing from './TicketingTabProcessing.vue'
import { OPEN_TAB_VALIDATION_CODES, VALIDATION_MESSAGES } from '@/constants'
import { getValidationCode } from '@/utils/helpers'

export default {
	name: 'CheckoutProcessing',

	mixins: [routerMixin],

	components: {
		PageHeader,
		ProcessingFailed,
		RegularTabProcessing,
		TicketingTabProcessing
	},

	data() {
		return {
			sessionCartInterval: null,
			isLoading: false,
			// TODO set to false before commit
			debugTicketingFlow: false,
			darkTheme: true
		}
	},

	computed: {
		...mapState('venue', ['venueInfo']),
		...mapState('cart', { cartId: 'id' }),
		...mapState('cart', [
			'customerNameToPrint',
			'isTabFailed',
			'isCartRecreated'
		]),
		...mapGetters('cart', [
			'isTabStatusCreated',
			'isWaitingPos',
			'isTabOpenedByRedemption'
		]),

		acceptOnlineOrder() {
			return this.venueInfo.acceptOnlineOrder
		},
		openingTabComponent() {
			return this.isTicketingFlow
				? 'ticketing-tab-processing'
				: 'regular-tab-processing'
		},
		isTicketingFlow() {
			const { eventTicketId } = this.$route.query || this.$route.params || {}
			return (
				this.debugTicketingFlow ||
				this.isTabOpenedByRedemption ||
				!!eventTicketId
			)
		}
	},

	async created() {
		if (!this.debugTicketingFlow) {
			if (this.acceptOnlineOrder && this.isWaitingPos) {
				this.setSessionCartInterval()
			} else {
				await this.goToProducts()
			}
		}
	},

	beforeDestroy() {
		clearInterval(this.sessionCartInterval)
	},

	watch: {
		isTabFailed(val) {
			if (val) {
				clearInterval(this.sessionCartInterval)
			}
		}
	},

	methods: {
		...mapActions('cart', ['openTab', 'getCart', 'toggleIsCartRecreated']),
		...mapActions('ui', ['addMainError']),

		async goBack() {
			await this.goToProducts()
		},

		setSessionCartInterval() {
			this.sessionCartInterval = setInterval(async () => {
				try {
					await this.getCart(true)

					if (this.isCartRecreated || this.isTabStatusCreated) {
						clearInterval(this.sessionCartInterval)
					}
				} catch (e) {}
			}, 5000)
		},

		async retryOpenTab() {
			try {
				this.isLoading = true
				await this.openTab({ cartId: this.cartId })

				if (this.sessionCartInterval) clearInterval(this.sessionCartInterval)

				this.setSessionCartInterval()
				this.isLoading = false
			} catch (e) {
				this.isLoading = false
				const validationCode = getValidationCode(e)

				if (OPEN_TAB_VALIDATION_CODES.includes(validationCode)) {
					await this.goBack(true)
				}

				if (validationCode === VALIDATION_MESSAGES.CART_NOT_FOUND) {
					this.toggleIsCartRecreated()
				}
			}
		}
	}
}
</script>

<style scoped lang="scss">
@import './src/assets/styles/variables';

.processing-wrap {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	background: #fff;
	text-align: center;
	max-width: 640px;
	margin: 0 auto;

	&--dark {
		background: $background-dark;
		color: $white;

		/deep/ .failed {
			.customer-name {
				color: $white;
			}
		}
	}
}

.processing-holder {
	flex-grow: 1;
	position: relative;
	overflow-y: auto;
}
</style>
